/**
 * Market Data PDF Generator
 * 
 * Main orchestrator for generating PDF exports from the Market Data page.
 * Handles header, filters, charts, and tables.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatFiltersForPDF } from './filterSummaryFormatter';
import { 
  drawProjectedPatientsChart, 
  drawSharedAffiliationChart,
  drawSharedAffiliationByServiceLineChart,
  drawPayorChart,
  drawAffiliationSummary,
} from './chartRenderer';
import { PDF_SETTINGS, getSettingsByType } from './pdfStyles';
import { addLogoToPDF, addFooterToAllPages } from './logoManager';

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'provider.location.city')
 */
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

/**
 * Extract text from a value that might be a React element
 */
const extractText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    // React element - try to get props.children or return empty
    if (value.props?.children) {
      if (typeof value.props.children === 'string') return value.props.children;
      if (Array.isArray(value.props.children)) {
        return value.props.children.map(extractText).join(' ');
      }
    }
    return '';
  }
  return String(value);
};

/**
 * Get human-readable label for patient type
 */
const getPatientTypeLabel = (patientType) => {
  const labels = {
    'ALL_PATIENTS': 'Projected Patients',
    'PEDIATRIC_PATIENTS': 'Captured Pediatric Patients',
    'CAPTURED_CLAIMS': 'Captured Claims',
  };
  return labels[patientType] || patientType || 'Projected Patients';
};

/**
 * Render filter summary in a compact two-column layout
 */
const renderFilterSummary = (doc, filterLines, startY, pageWidth) => {
  if (!filterLines.length) return startY;
  
  const x = PDF_SETTINGS.margin.left;
  const col2X = pageWidth / 2;
  let y = startY;
  
  doc.setFontSize(PDF_SETTINGS.header.filterFontSize);
  
  filterLines.forEach((filter, idx) => {
    const colX = idx % 2 === 0 ? x : col2X;
    
    // Label (bold)
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_SETTINGS.colors.text.secondary);
    doc.text(`${filter.label}:`, colX, y);
    
    // Value (normal)
    const labelWidth = doc.getTextWidth(`${filter.label}: `);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...PDF_SETTINGS.colors.text.primary);
    
    // Truncate long values
    let value = filter.value;
    const maxValueWidth = (pageWidth / 2) - labelWidth - 20;
    while (doc.getTextWidth(value) > maxValueWidth && value.length > 10) {
      value = value.substring(0, value.length - 4) + '...';
    }
    
    doc.text(value, colX + labelWidth, y);
    
    // Move to next row after every 2 items
    if (idx % 2 === 1) {
      y += 14;
    }
  });
  
  // Handle odd number of filters
  if (filterLines.length % 2 === 1) {
    y += 14;
  }
  
  return y;
};

/**
 * Main PDF generation function for Market Data reports
 * 
 * @param {Object} config - Configuration object
 * @returns {Promise<jsPDF>} - The generated PDF document
 */
export const generateMarketDataPDF = async (config) => {
  const {
    // Report metadata
    title = 'Market Data Report',
    reportType = 'projected_patients',
    
    // Data
    chartData = [],           // Array from CLAIMS_PER_PROVIDER or similar
    providerCounts = null,    // { total, total_affiliated }
    tableData = [],           // For table-based reports
    columns = [],             // DynamicTable column definitions
    payors = [],              // For payors chart - array of {id, name, color}
    
    // Filters
    filters = {},
    
    // Options
    options = {},
  } = config;

  const {
    orientation = 'landscape',
    pageSize = 'letter',
    includeHeader = true,
    includeLogo = true,  // Controlled separately or follows includeHeader
    includeFooter = true, // New option for footer
    includeChart = true,
    includeTable = false,
    maxRows = 200,
    patientType = 'ALL_PATIENTS',
    claimsType = 'providers',
    pdfType = 'printable',  // 'slideshow' or 'printable'
  } = options;

  // Get settings based on pdfType
  const settings = getSettingsByType(pdfType);

  // Create PDF document
  const doc = new jsPDF({
    orientation,
    unit: 'pt',
    format: pageSize,
  });

  const pageWidth = doc.internal.pageSize.width;
  let yPosition = settings.margin.top;

  // === HEADER ===
  if (includeHeader) {
    // Add logo on the RIGHT side (own line)
    if (includeLogo) {
      const logoMaxWidth = settings.logo.maxWidth;
      const logoMaxHeight = settings.logo.maxHeight;
      const logoX = pageWidth - settings.margin.right - logoMaxWidth;
      await addLogoToPDF(doc, { 
        x: logoX, 
        y: yPosition - 15,
        maxWidth: logoMaxWidth,
        maxHeight: logoMaxHeight,
      });
      yPosition += settings.logo.maxHeight - 5;  // Move down after logo
    }

    // Build the title based on report type
    let dynamicTitle = title || '';
    
    // Get typeLabel for potential suffix
    const typeLabel = claimsType === 'organizations' ? 'Organizations' : 'Providers';
    
    // If no custom title provided, or title matches default patterns, build dynamic title
    if (!title || title === 'Market Data Export' || title === 'Market Data Report' || title.startsWith('Market Data -')) {
      // Check multiple sources for patient type
      let effectivePatientType = filters.patientType || patientType;
      if (!effectivePatientType || effectivePatientType === 'ALL_PATIENTS') {
        // Also check reportType
        if (reportType === 'captured_claims') {
          effectivePatientType = 'CAPTURED_CLAIMS';
        }
      }
      const patientsLabel = getPatientTypeLabel(effectivePatientType);
      const serviceLine = filters.serviceLine?.name || filters.serviceLine?.label || 
        (typeof filters.serviceLine === 'string' ? filters.serviceLine : '');
      
      // Extract market name - check multiple possible fields
      let market = '';
      if (filters.market) {
        if (typeof filters.market === 'string') {
          market = filters.market;
        } else {
          // Check various possible field names for market name
          market = filters.market.name || 
                   filters.market.label || 
                   filters.market.market_name || 
                   filters.market.display_name ||
                   filters.market.title ||
                   '';
        }
      }
      
      // Build title based on report type
      if (reportType === 'payors' || reportType === 'payor_mix') {
        dynamicTitle = 'Payors - ' + patientsLabel;
      } else if (reportType === 'shared_affiliation') {
        dynamicTitle = 'Shared Affiliation - ' + patientsLabel;
      } else {
        dynamicTitle = patientsLabel;
      }
      
      // Add service line to title (but not for Captured Claims by Organization - they don't have service line filter)
      const isCapturedClaimsByOrg = (reportType === 'captured_claims' || effectivePatientType === 'CAPTURED_CLAIMS') && claimsType === 'organizations';
      if (!isCapturedClaimsByOrg && serviceLine && serviceLine !== 'all' && serviceLine !== 'All Service Lines') {
        dynamicTitle += ` by ${serviceLine}`;
      }
      if (market) {
        dynamicTitle += ` in ${market}`;
      }
      dynamicTitle += ` by ${typeLabel}`;
    } else {
      // Custom title provided - ensure it ends with "by Providers" or "by Organizations" if applicable
      if (reportType !== 'payors' && reportType !== 'payor_mix' && reportType !== 'shared_affiliation') {
        if (!dynamicTitle.includes('by Providers') && !dynamicTitle.includes('by Organizations')) {
          dynamicTitle += ` by ${typeLabel}`;
        }
      }
    }

    // Title (below logo)
    doc.setFontSize(settings.header.titleFontSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(dynamicTitle, settings.margin.left, yPosition);
    yPosition += settings.header.titleFontSize + 4;

    // Build comprehensive subtitle from all filters
    const subtitleParts = [];
    
    // Care Setting
    if (filters.careSetting && filters.careSetting !== 'All Care Settings' && filters.careSetting !== 'All') {
      subtitleParts.push(filters.careSetting);
    }
    
    // Specialties - handle multiple formats
    if (filters.specialty && filters.specialty !== 'All Specialties' && filters.specialty !== 'All') {
      subtitleParts.push(filters.specialty);
    } else if (filters.specialties && Array.isArray(filters.specialties) && filters.specialties.length > 0) {
      const specNames = filters.specialties.map(s => s.name || s.label || s).filter(Boolean);
      if (specNames.length > 0 && specNames.length <= 3) {
        subtitleParts.push(specNames.join(', '));
      } else if (specNames.length > 3) {
        subtitleParts.push(`${specNames.length} Specialties`);
      }
    } else if (filters.taxonomies && Array.isArray(filters.taxonomies) && filters.taxonomies.length > 0) {
      // Taxonomies are specialty codes - display count or names if available
      const taxCount = filters.taxonomies.length;
      if (taxCount === 1) {
        // Try to get the name if it's an object, otherwise show code
        const tax = filters.taxonomies[0];
        const taxName = tax.name || tax.label || tax.classification || tax;
        subtitleParts.push(typeof taxName === 'string' ? taxName : `${taxCount} Specialty`);
      } else if (taxCount <= 3) {
        const taxNames = filters.taxonomies.map(t => t.name || t.label || t.classification || t).filter(Boolean);
        subtitleParts.push(taxNames.join(', '));
      } else {
        subtitleParts.push(`${taxCount} Specialties`);
      }
    }
    
    // Affiliation
    if (filters.affiliation) {
      if (filters.affiliation === 'affiliated') {
        subtitleParts.push('Affiliated Only');
      } else if (filters.affiliation === 'non-affiliated') {
        subtitleParts.push('Non-Affiliated Only');
      }
    }
    
    // Payors (for payor chart)
    if ((reportType === 'payors' || reportType === 'payor_mix') && payors && payors.length > 0) {
      const payorNames = payors.map(p => p.name).filter(Boolean);
      if (payorNames.length > 0 && payorNames.length <= 3) {
        subtitleParts.push(payorNames.join(', '));
      } else if (payorNames.length > 3) {
        subtitleParts.push(`${payorNames.length} Payors`);
      }
    }
    
    // Claims Date (before/after)
    if (filters.claimsDate !== undefined && filters.claimsDate !== null) {
      subtitleParts.push(filters.claimsDate ? 'Claims Before' : 'Claims After');
    }
    
    // Tag
    if (filters.tag) {
      const tagName = filters.tag.name || filters.tag.label || (typeof filters.tag === 'string' ? filters.tag : '');
      if (tagName) {
        subtitleParts.push(`Tag: ${tagName}`);
      }
    }
    
    // Service Line (if not already in title and not Captured Claims by Organization)
    const isCapturedClaimsByOrg = (reportType === 'captured_claims' || filters.patientType === 'CAPTURED_CLAIMS') 
                                  && claimsType === 'organizations';
    if (filters.serviceLine && !isCapturedClaimsByOrg) {
      const slName = filters.serviceLine.name || filters.serviceLine.label || 
        (typeof filters.serviceLine === 'string' ? filters.serviceLine : '');
      if (slName && slName !== 'all' && slName !== 'All Service Lines' && slName !== 'All') {
        // Only add if not already shown
        if (!subtitleParts.some(p => p.includes(slName))) {
          subtitleParts.push(slName);
        }
      }
    }
    
    // Build subtitle string
    let subtitle = '';
    if (subtitleParts.length > 0) {
      subtitle = 'By ' + subtitleParts.join(' | ');
    } else {
      // Default subtitle if no specific filters
      subtitle = 'By All Care Settings and All Specialties';
    }
    
    doc.setFontSize(settings.header.subtitleFontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(subtitle, settings.margin.left + 10, yPosition + 5);
    yPosition += settings.header.subtitleFontSize + 20;
  } else {
    // If no header, just add some top margin
    yPosition += 20;
  }

  // === AFFILIATION SUMMARY (for Projected Patients) ===
  if (providerCounts && reportType === 'projected_patients') {
    yPosition = drawAffiliationSummary(doc, providerCounts, settings.margin.left, yPosition, { pdfType });
  }

  // === CHART ===
  // Ensure chartData is an array - extract rows if needed
  let chartRows = chartData;
  if (!Array.isArray(chartData)) {
    chartRows = chartData?.rows || chartData?.data?.rows || [];
  }
  
  if (includeChart && chartRows.length > 0) {
    switch (reportType) {
      case 'projected_patients':
      case 'captured_claims':
        yPosition = drawProjectedPatientsChart(doc, chartRows, {
          x: settings.margin.left,
          y: yPosition,
          maxRows,
          patientType,
          claimsType,
          showLegend: patientType !== 'CAPTURED_CLAIMS' && patientType !== 'PEDIATRIC_PATIENTS',
          pdfType,
        });
        break;
        
      case 'shared_affiliation':
        // Check if this is by service lines or by providers
        if (claimsType === 'servicelines') {
          yPosition = drawSharedAffiliationByServiceLineChart(doc, chartRows, {
            x: settings.margin.left,
            y: yPosition,
            maxRows,
            showLegend: true,
            pdfType,
          });
        } else {
          yPosition = drawSharedAffiliationChart(doc, chartRows, {
            x: settings.margin.left,
            y: yPosition,
            maxRows,
            showLegend: true,
            pdfType,
          });
        }
        break;
        
      case 'payors':
      case 'payor_mix':
        yPosition = drawPayorChart(doc, chartRows, payors, {
          x: settings.margin.left,
          y: yPosition,
          maxRows,
          showLegend: true,
          claimsType,
          pdfType,
        });
        break;
        
      default:
        // Fall back to projected patients chart
        yPosition = drawProjectedPatientsChart(doc, chartRows, {
          x: settings.margin.left,
          y: yPosition,
          maxRows,
          patientType,
          claimsType,
          pdfType,
        });
    }
  }

  // === TABLE (for other report types or if explicitly requested) ===
  if (includeTable && tableData.length > 0 && columns.length > 0) {
    // Filter out columns that shouldn't be exported
    const exportColumns = columns.filter(col => 
      col.key && col.label && !col.hideInExport
    );

    // Format table headers
    const headers = exportColumns.map(col => col.label);

    // Format table body
    const body = tableData.slice(0, maxRows).map(row => {
      return exportColumns.map(col => {
        const value = getNestedValue(row, col.key);
        if (col.format) {
          return extractText(col.format(value, row));
        }
        return value ?? '';
      });
    });

    // Draw table using autoTable
    autoTable(doc, {
      startY: yPosition,
      head: [headers],
      body: body,
      styles: {
        fontSize: PDF_SETTINGS.table.fontSize,
        cellPadding: PDF_SETTINGS.table.cellPadding,
      },
      headStyles: {
        fillColor: PDF_SETTINGS.table.headerBgColor,
        textColor: PDF_SETTINGS.colors.text.white,
        fontStyle: 'bold',
        fontSize: PDF_SETTINGS.table.headerFontSize,
      },
      alternateRowStyles: {
        fillColor: PDF_SETTINGS.table.alternateBgColor,
      },
      showHead: 'everyPage',
      margin: {
        top: settings.margin.top,
        left: settings.margin.left,
        right: settings.margin.right,
      },
      didDrawPage: () => {}, // Footer added at the end
    });
  }

  // Add footer with logo and page numbers to all pages (if enabled)
  if (includeFooter) {
    await addFooterToAllPages(doc, { pdfType });
  }

  return doc;
};

/**
 * Generate and download PDF
 */
export const downloadMarketDataPDF = async (config) => {
  const doc = await generateMarketDataPDF(config);
  const filename = config.filename || `market-data-${Date.now()}.pdf`;
  doc.save(filename);
};

/**
 * Generate PDF and return as blob URL for preview
 */
export const previewMarketDataPDF = async (config) => {
  const doc = await generateMarketDataPDF(config);
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
};

export default {
  generateMarketDataPDF,
  downloadMarketDataPDF,
  previewMarketDataPDF,
};